import * as ko from "knockout";
import {IObjectType, IRouteDefinitionParams, IRule, IRuleset} from "../../../index";
import {global} from "../../../appContext";
import {MutableRule} from "./rule/MutableRule";
import {nsRule} from "./rule/nsRule";
import {PermissionValidator} from "../../../auth/PermissionValidator";
import {AvailableData} from "./rule/AvailableData";
import {MutableRuleset} from "./mutableRuleset";
import {ObjectTypeId} from "../../../constants";
import {nsRoute} from "../../../util/route";
import {storeObservable} from "../../../util/sessionstore";
import {IWizardParams} from "../index";
import {IDerivedMutableParams} from "../derived/index";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import rulesHtml from './rules.html?raw';

export let template = rulesHtml;

interface IParams extends IRouteDefinitionParams {
    availableData: AvailableData
    mutableRuleset: MutableRuleset,
    objectType: KnockoutObservable<IObjectType>
}

export class viewModel extends ParsedRouteDataDecorator {
    private availableData!: AvailableData;
    private objectType: KnockoutObservable<IObjectType>;
    private pv: PermissionValidator = global.permissionValidator;
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly toggleDerivedValues: KnockoutObservable<boolean> = storeObservable("ruleset.toggle.derived", true);
    private reqGetConditions = global.getPromiseRequest("getconditions");

    // array of rules data
    private data: KnockoutObservableArray<MutableRule>;

    // last moved up/down observables to help us keep focus on the up/down buttons
    lastMovedUp: KnockoutObservable<MutableRule | null> = ko.observable(null);
    lastMovedDown: KnockoutObservable<MutableRule | null> = ko.observable(null);

    constructor(private params: IParams) {
        super(params);
        this.objectType = params.objectType;
        this.data = this.params.mutableRuleset.data.rules;

        if ('availableData' in params) {
            this.availableData = params.availableData;
            this.isReady(true);
        } else {
            this.isReady(false);
            this.availableData
                .init()
                .then(() => this.isReady(true))
            ;
        }
    }

    onUpClick(item: MutableRule) {
        const index = this.data().indexOf(item);
        this.swap(index, index - 1);
        setTimeout(() => this.lastMovedUp(item), 1);
    }

    onDownClick(item: MutableRule) {
        const index = this.data().indexOf(item);
        this.swap(index, index + 1);
        setTimeout(() => this.lastMovedDown(item), 1);
    }

    swap(index1: number, index2: number) {
        const item = this.data()[index1];
        if (!item) {
            return;
        }
        const isFirstItem = Math.min(index1, index2) == 0;
        if (isFirstItem) {
            const otherRule = this.data()[Math.max(index1, index2)];
            const firstRule = this.data()[0];
            if (otherRule && firstRule) {
                firstRule.booleanoperator(otherRule.booleanoperator());
            }
        }
        this.lastMovedUp(null);
        this.lastMovedDown(null);
        this.data.splice(index1, 1);
        this.data.splice(index2, 0, item);
        this.order();
    }

    private order() {
        this.data().forEach((item: MutableRule, index: number) => {
            if (index == 0) {
                // the first rule should never have a boolean operator
                item.booleanoperator('');
            }
            item.order(index + 1);
        });
    }

    onAddClick() {
        const ruleMutable = new MutableRule(this.availableData);
        ruleMutable.initErrorTracking();
        this.data.push(ruleMutable);
        this.order();
    }

    onRemoveClick(item: MutableRule) {
        const index = this.data.indexOf(item);
        item.dispose();
        this.data.remove(item);
        this.order();
        global.snackbarData({
            message: 'subject is deleted|s:((rule))',
            undo: () => {
                this.data.splice(index, 0, item);
                item.initErrorTracking();
            }
        });
    }

    isFirst(item: MutableRule): boolean {
        return this.data()[0] == item;
    }

    isLast(item: MutableRule): boolean {
        return this.data()[this.data().length - 1] == item;
    }

    isRulesetCondition(item: MutableRule, type: string): boolean {
        return this.availableData.getArgumentType(item.argumentoperator() ?? '') == type;
    }

    private doDialogPickObjectAttribute(ruleMutable: MutableRule) {
        nsRule.doDialogPickObjectAttributeValue((val: any) => {
            ruleMutable.argument(val);
        }, ruleMutable);
    }

    private getWizardParams(): IWizardParams {
        return {
            __dialog: {
                callbackFn: (rules: IRule[]) => {
                    const total = this.data().length;
                    rules.forEach((r: IRule, index: number) => {
                        const shouldUseBooleanOperator = total || index > 0;
                        r.booleanoperator = shouldUseBooleanOperator ? 'AndAnd' : '';
                        r.order = total + index + 1;
                    });
                    const newRules = rules.map((r: IRule) => new MutableRule(this.availableData, r));
                    newRules.forEach(o => o.initErrorTracking());
                    this.data.push(...newRules);
                    this.order();
                },
                width: 'clamp(1em, 50em, 90vw)',
            },
            availableData: this.availableData,
            mutableRuleset: this.params.mutableRuleset,
            objectType: this.objectType,
        }
    }


    private onWizardClick() {
        global.dialogManager.addCustom({
            name: 'component/ruleset/wizard/manager',
            params: this.getWizardParams(),
        })
    }

    private isWizardSupported(): boolean {
        return [ObjectTypeId.User, ObjectTypeId.Department, ObjectTypeId.Group].includes(this.objectType().id);
    }

    private getStaticRuleset(): IRuleset {
        const mrs = this.params.mutableRuleset.data;
        return {
            authdatasources: mrs.authdatasources(),
            companyid: mrs.companyid(),
            departmentexpression: mrs.departmentexpression(),
            description: mrs.description(),
            evaluatepositions: mrs.evaluatepositions(),
            id: mrs.$original.id,
            name: mrs.name(),
            objecttype: mrs.objectType()!.type,
            refs: mrs.$original.refs,
            rules: mrs.rules().map((r: MutableRule): IRule => ({
                argument: r.argument() ?? '',
                argumentoperator: (r.argumentoperator() ?? '') as IRule['argumentoperator'],
                attribute: r.attribute() ?? '',
                booleanoperator: r.booleanoperator() ?? '',
                id: r.$original.id,
                order: r.order() ?? 0,
                rulesetid: mrs.$original.id,
                rulesetpath: r.$original.rulesetpath,
            }))
        }
    }

    private getStaticRule(rule: MutableRule): IRule {
        return {
            argument: rule.argument() ?? '',
            argumentoperator: (rule.argumentoperator() ?? '') as IRule['argumentoperator'],
            attribute: rule.attribute() ?? '',
            booleanoperator: rule.booleanoperator() ?? '',
            id: rule.$original.id,
            order: rule.order() ?? 0,
            rulesetid: this.params.mutableRuleset.data.$original.id,
            rulesetpath: rule.$original.rulesetpath,
        }
    }

    private getDerivedParams(mutableRule: MutableRule): IDerivedMutableParams {
        return {
            rule: mutableRule,
            ruleset: this.availableData.ruleset,
            conditions: this.availableData.conditions,
        }
    }

    override dispose() {
        this.reqGetConditions.abort();
    }
}
